import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, CircularProgress, Chip, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, Tooltip,
  Select, MenuItem, FormControl, InputLabel, Divider, Collapse,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  LinearProgress, List, ListItem, ListItemText, Switch, FormControlLabel,
  Grid,
} from '@mui/material';
import SyncIcon         from '@mui/icons-material/Sync';
import PlayArrowIcon    from '@mui/icons-material/PlayArrow';
import UndoIcon         from '@mui/icons-material/Undo';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon   from '@mui/icons-material/ExpandMore';
import ExpandLessIcon   from '@mui/icons-material/ExpandLess';
import StorageIcon      from '@mui/icons-material/Storage';
import CloudSyncIcon    from '@mui/icons-material/CloudSync';
import CodeIcon         from '@mui/icons-material/Code';
import WebIcon          from '@mui/icons-material/Language';
import FolderOpenIcon   from '@mui/icons-material/FolderOpen';
import CameraAltIcon    from '@mui/icons-material/CameraAlt';
import LockOpenIcon     from '@mui/icons-material/LockOpen';
import SecurityIcon     from '@mui/icons-material/Security';
import api from '../utils/api';

// ── Small helpers ─────────────────────────────────────────────────────────

function Badge({ label, bg, color }) {
  return (
    <Chip label={label} size="small"
      sx={{ height:20, fontSize:'0.65rem', fontWeight:700, backgroundColor:bg, color, borderRadius:'5px', ml:0.5 }} />
  );
}

function SectionPanel({ title, icon, count, countBg, countColor, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Paper elevation={0} sx={{ borderRadius:'12px', border:'1px solid rgba(47,99,173,0.1)', overflow:'hidden', mb:2 }}>
      <Box onClick={() => setOpen(v => !v)} sx={{ px:2.5, py:1.5, display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', '&:hover':{ backgroundColor:'#f8fafc' } }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          {icon}
          <Typography sx={{ fontWeight:700, fontSize:'0.9rem', color:'#1e3a5f' }}>{title}</Typography>
          {count !== undefined && <Badge label={count} bg={countBg || '#e2e8f0'} color={countColor || '#475569'} />}
        </Box>
        <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>
      <Collapse in={open}>
        <Divider />
        <Box sx={{ p:2 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
}

function LogDialog({ open, onClose, log, title, loading }) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx:{ borderRadius:'16px' } }}>
      <DialogTitle sx={{ fontWeight:700, color:'#1e3a5f' }}>{title}</DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb:2, borderRadius:4 }} />}
        <Box sx={{ backgroundColor:'#0f1f35', borderRadius:'10px', p:2, maxHeight:420, overflowY:'auto' }}>
          <List dense disablePadding>
            {(log || []).map((line, i) => (
              <ListItem key={i} disablePadding sx={{ py:0.15 }}>
                <ListItemText primary={line}
                  primaryTypographyProps={{ sx:{ fontFamily:'monospace', fontSize:'0.78rem', lineHeight:1.5,
                    color: line.startsWith('FATAL') || line.includes('✗') ? '#f87171'
                         : line.includes('✓') ? '#4ade80'
                         : line.startsWith('--') ? '#94a3b8'
                         : '#e2e8f0' } }} />
              </ListItem>
            ))}
            {loading && (
              <ListItem disablePadding>
                <ListItemText primary="…" primaryTypographyProps={{ sx:{ fontFamily:'monospace', fontSize:'0.78rem', color:'#94a3b8' } }} />
              </ListItem>
            )}
          </List>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px:3, pb:2 }}>
        <Button onClick={onClose} disabled={loading} sx={{ borderRadius:'10px', textTransform:'none', fontWeight:600 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Plan detail sub-components ────────────────────────────────────────────

function RenamesTable({ renames }) {
  if (!renames?.length) return <Typography variant="body2" sx={{ color:'#9aa5b4', fontStyle:'italic' }}>No table renames needed.</Typography>;
  return (
    <Table size="small">
      <TableHead><TableRow sx={{ backgroundColor:'#f8fafc' }}>
        <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Current name on tenant</TableCell>
        <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>→  Target name (matches demo)</TableCell>
      </TableRow></TableHead>
      <TableBody>
        {renames.map(r => (
          <TableRow key={r.from}>
            <TableCell sx={{ fontFamily:'monospace', fontSize:'0.78rem', color:'#92400e' }}>{r.from}</TableCell>
            <TableCell sx={{ fontFamily:'monospace', fontSize:'0.78rem', color:'#166534', fontWeight:700 }}>{r.to}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function AddColumnsTable({ addColumns }) {
  if (!addColumns?.length) return <Typography variant="body2" sx={{ color:'#9aa5b4', fontStyle:'italic' }}>No missing columns.</Typography>;
  return (
    <Table size="small">
      <TableHead><TableRow sx={{ backgroundColor:'#f8fafc' }}>
        <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Table</TableCell>
        <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Column</TableCell>
        <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Type</TableCell>
        <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Null</TableCell>
      </TableRow></TableHead>
      <TableBody>
        {addColumns.flatMap(t => t.columns.map(col => (
          <TableRow key={`${t.demoTableName}.${col.name}`}>
            <TableCell sx={{ fontFamily:'monospace', fontSize:'0.75rem', color:'#1e3a5f', fontWeight:600 }}>{t.demoTableName}</TableCell>
            <TableCell sx={{ fontFamily:'monospace', fontSize:'0.75rem', color:'#1d4ed8' }}>{col.name}</TableCell>
            <TableCell sx={{ fontFamily:'monospace', fontSize:'0.72rem', color:'#6b778c' }}>{col.type}</TableCell>
            <TableCell>
              <Chip label={col.nullable ? 'NULL' : 'NOT NULL'} size="small"
                sx={{ height:18, fontSize:'0.62rem', backgroundColor: col.nullable ? '#f0fdf4' : '#fff7ed', color: col.nullable ? '#166534' : '#c2410c', borderRadius:'4px' }} />
            </TableCell>
          </TableRow>
        )))}
      </TableBody>
    </Table>
  );
}

function MissingTablesTable({ missingTables }) {
  if (!missingTables?.length) return <Typography variant="body2" sx={{ color:'#9aa5b4', fontStyle:'italic' }}>No missing tables.</Typography>;
  return (
    <Table size="small">
      <TableHead><TableRow sx={{ backgroundColor:'#f8fafc' }}>
        <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Table</TableCell>
        <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Columns</TableCell>
        <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Action</TableCell>
      </TableRow></TableHead>
      <TableBody>
        {missingTables.map(t => (
          <TableRow key={t.demoName}>
            <TableCell sx={{ fontFamily:'monospace', fontSize:'0.78rem', color:'#991b1b', fontWeight:700 }}>{t.demoName}</TableCell>
            <TableCell sx={{ fontSize:'0.75rem', color:'#6b778c' }}>{t.columns.length} columns</TableCell>
            <TableCell><Chip label="CREATE TABLE auto-generated ✓" size="small" sx={{ height:20, fontSize:'0.65rem', backgroundColor:'#dcfce7', color:'#166534', borderRadius:'5px' }} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function FilesToPushTable({ filesToPush }) {
  if (!filesToPush?.length) return <Typography variant="body2" sx={{ color:'#9aa5b4', fontStyle:'italic' }}>All files match demo.</Typography>;
  const backend  = filesToPush.filter(f => f.category === 'backend');
  const frontend = filesToPush.filter(f => f.category === 'frontend');
  return (
    <Box sx={{ display:'flex', flexDirection:'column', gap:1.5 }}>
      {backend.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight:700, color:'#6b778c', textTransform:'uppercase', letterSpacing:0.5 }}>Backend ({backend.length})</Typography>
          {backend.map(f => (
            <Box key={f.file} sx={{ display:'flex', alignItems:'center', gap:1, py:0.3 }}>
              <Badge label={f.isNew ? 'NEW' : 'CHANGED'} bg={f.isNew ? '#dbeafe' : '#fef3c7'} color={f.isNew ? '#1d4ed8' : '#92400e'} />
              <Typography sx={{ fontFamily:'monospace', fontSize:'0.78rem', color:'#1e3a5f' }}>{f.file}</Typography>
            </Box>
          ))}
        </Box>
      )}
      {frontend.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight:700, color:'#6b778c', textTransform:'uppercase', letterSpacing:0.5 }}>Frontend source ({frontend.length} files differ)</Typography>
          <Alert severity="info" sx={{ mt:0.5, py:0.5, px:1.5, borderRadius:'8px', fontSize:'0.78rem' }}>
            The full frontend build will be rsynced to the tenant's nginx folder - not just these individual files.
          </Alert>
        </Box>
      )}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────

export default function Sync() {
  const [tenants, setTenants]         = useState([]);
  const [tenantId, setTenantId]       = useState('');
  const [generating, setGenerating]   = useState(false);
  const [planData, setPlanData]       = useState(null);  // null | { plan, sql, rollbackSql, filesToPush, dbWarnings, summary }
  const [deploying, setDeploying]     = useState(false);
  const [pushingFE, setPushingFE]     = useState(false);
  const [includeFE, setIncludeFE]     = useState(true);
  const [includeSQL, setIncludeSQL]   = useState(true);
  const [snapshots, setSnapshots]     = useState([]);
  const [rollingBack, setRollingBack] = useState(false);
  const [mergeEnv, setMergeEnv]       = useState(true);
  const [upgradeNode, setUpgradeNode] = useState(false);
  const [syncingData, setSyncingData] = useState(false);
  const [grantingPerms, setGrantingPerms] = useState(false);
  const [syncingPerms, setSyncingPerms]     = useState(false);
  const [assigningTta, setAssigningTta]   = useState(false);
  const [logDialog, setLogDialog]     = useState({ open:false, title:'', log:[], loading:false });

  useEffect(() => {
    api.get('/tenants').then(r => {
      setTenants((r.data || []).filter(t => t.slug !== 'demo' && t.is_active));
    }).catch(() => {});
  }, []);

  const loadSnapshots = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await api.get(`/sync/${id}/snapshots`);
      setSnapshots(r.data.snapshots || []);
    } catch { setSnapshots([]); }
  }, []);

  const selectTenant = (id) => {
    setTenantId(id);
    setPlanData(null);
    loadSnapshots(id);
  };

  const generatePlan = async () => {
    setGenerating(true);
    setPlanData(null);
    try {
      const r = await api.get(`/sync/${tenantId}/plan`);
      setPlanData(r.data);
    } catch (err) {
      setPlanData({ error: err.response?.data?.message || err.message });
    } finally { setGenerating(false); }
  };

  // Deploy = auto-snapshot then apply in one shot
  const deploy = async () => {
    if (!planData) return;
    setDeploying(true);
    const log = ['Creating snapshot…'];
    setLogDialog({ open:true, title:'Deploying to ' + selectedTenant?.name, log, loading:true });

    try {
      // 1. Auto snapshot
      const snapRes = await api.post(`/sync/${tenantId}/snapshot`, {
        sql: planData.sql, rollbackSql: planData.rollbackSql, plan: planData.plan,
      });
      const snapId = snapRes.data.snapshotId;
      log.push(`✓ Snapshot created: ${snapId}`);
      setLogDialog(d => ({ ...d, log: [...log] }));

      // 2. Apply (SQL + backend files + optional frontend)
      log.push('Applying changes…');
      setLogDialog(d => ({ ...d, log: [...log] }));

      const applyRes = await api.post(`/sync/${tenantId}/apply`, {
        snapshotId: snapId,
        sql: includeSQL ? planData.sql : '-- SQL skipped by user',
        dataSyncSql: planData.dataSyncSql || '',
        filesToPush: planData.filesToPush,
        pushFrontend: includeFE,
        mergeEnv,
        upgradeNode,
      });

      const finalLog = [...log, ...( applyRes.data.log || [])];
      await loadSnapshots(tenantId);
      setLogDialog({ open:true, title:'Deploy Complete ✓', log: finalLog, loading:false });
    } catch (err) {
      const errLog = [...log, ...(err.response?.data?.log || [`FATAL: ${err.response?.data?.message || err.message}`])];
      setLogDialog({ open:true, title:'Deploy Failed', log: errLog, loading:false });
    } finally { setDeploying(false); }
  };

  const pushFrontendOnly = async () => {
    setPushingFE(true);
    setLogDialog({ open:true, title:'Pushing Frontend', log:['Starting rsync…'], loading:true });
    try {
      const r = await api.post(`/sync/${tenantId}/push-frontend`);
      setLogDialog({ open:true, title:'Frontend Pushed ✓', log: r.data.log || ['Done.'], loading:false });
    } catch (err) {
      const errLog = err.response?.data?.log || [`FATAL: ${err.response?.data?.message || err.message}`];
      setLogDialog({ open:true, title:'Frontend Push Failed', log: errLog, loading:false });
    } finally { setPushingFE(false); }
  };

  const syncDataOnly = async () => {
    setSyncingData(true);
    setLogDialog({ open:true, title:'Syncing System Table Data…', log:['Connecting…'], loading:true });
    try {
      const r = await api.post(`/sync/${tenantId}/sync-data`);
      setLogDialog({ open:true, title:'Data Sync Complete ✓', log: r.data.log || ['Done.'], loading:false });
    } catch (err) {
      const errLog = err.response?.data?.log || [`FATAL: ${err.response?.data?.message || err.message}`];
      setLogDialog({ open:true, title:'Data Sync Failed', log: errLog, loading:false });
    } finally { setSyncingData(false); }
  };

  const grantDbPermissions = async () => {
    setGrantingPerms(true);
    setLogDialog({ open:true, title:'Granting DB Permissions…', log:['Connecting…'], loading:true });
    try {
      const r = await api.post(`/sync/${tenantId}/grant-db-permissions`);
      setLogDialog({ open:true, title:'Permissions Granted ✓', log: r.data.log || ['Done.'], loading:false });
    } catch (err) {
      const errLog = err.response?.data?.log || [`FATAL: ${err.response?.data?.message || err.message}`];
      setLogDialog({ open:true, title:'Permission Grant Failed', log: errLog, loading:false });
    } finally { setGrantingPerms(false); }
  };

  const assignTtaadminPerms = async () => {
    setAssigningTta(true);
    setLogDialog({ open:true, title:'Assigning Permissions to ttaadmin…', log:['Connecting…'], loading:true });
    try {
      const r = await api.post(`/sync/${tenantId}/assign-ttaadmin-permissions`);
      setLogDialog({ open:true, title:'ttaadmin Permissions Assigned ✓', log: r.data.log || ['Done.'], loading:false });
    } catch (err) {
      const errLog = err.response?.data?.log || [`FATAL: ${err.response?.data?.message || err.message}`];
      setLogDialog({ open:true, title:'Assignment Failed', log: errLog, loading:false });
    } finally { setAssigningTta(false); }
  };

  const syncPermissions = async () => {
    setSyncingPerms(true);
    setLogDialog({ open:true, title:'Syncing App Permissions…', log:['Connecting to demo and tenant…'], loading:true });
    try {
      const r = await api.post(`/sync/${tenantId}/sync-permissions`);
      setLogDialog({ open:true, title:'Permissions Synced ✓', log: r.data.log || ['Done.'], loading:false });
    } catch (err) {
      const errLog = err.response?.data?.log || [`FATAL: ${err.response?.data?.message || err.message}`];
      setLogDialog({ open:true, title:'Permission Sync Failed', log: errLog, loading:false });
    } finally { setSyncingPerms(false); }
  };

  const rollback = async (snapId) => {
    setRollingBack(true);
    setLogDialog({ open:true, title:'Rolling Back…', log:['Starting rollback…'], loading:true });
    try {
      const r = await api.post(`/sync/${tenantId}/rollback/${snapId}`);
      setPlanData(null);
      setLogDialog({ open:true, title:'Rollback Complete ✓', log: r.data.log || ['Done.'], loading:false });
    } catch (err) {
      const errLog = err.response?.data?.log || [`FATAL: ${err.response?.data?.message || err.message}`];
      setLogDialog({ open:true, title:'Rollback Failed', log: errLog, loading:false });
    } finally { setRollingBack(false); }
  };

  const selectedTenant = tenants.find(t => t.id === tenantId);
  const nodeMismatch = planData?.nodeVersion?.mismatch;
  const plan = planData?.plan;
  const totalColsToAdd = plan?.addColumns?.reduce((n,t) => n + t.columns.length, 0) || 0;
  const totalChanges = (plan?.renames?.length||0) + totalColsToAdd + (plan?.missingTables?.length||0) + (planData?.filesToPush?.length||0);
  const inSync = planData && !planData.error && totalChanges === 0 && !planData.dbWarnings?.length;
  const canDeploy = planData && !planData.error && !deploying && (totalChanges > 0 || includeFE);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb:3 }}>
        <Typography variant="h4" sx={{ fontWeight:800, color:'#1e3a5f' }}>Deploy</Typography>
        <Typography variant="body2" sx={{ color:'#6b778c', mt:0.5 }}>
          Demo is the source of truth. Generate a diff plan, review it, then deploy to any tenant - schema, backend and frontend in one shot, with automatic snapshot and rollback.
        </Typography>
      </Box>

      {/* ── Tenant selector + actions ── */}
      <Paper elevation={0} sx={{ borderRadius:'16px', border:'1px solid rgba(47,99,173,0.08)', p:3, mb:3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm="auto">
            <FormControl size="small" sx={{ minWidth:220 }}>
              <InputLabel>Target Tenant</InputLabel>
              <Select value={tenantId} label="Target Tenant" onChange={e => selectTenant(e.target.value)}>
                {tenants.map(t => (
                  <MenuItem key={t.id} value={t.id}>
                    <Box>
                      <Typography sx={{ fontWeight:700, fontSize:'0.88rem' }}>{t.name}</Typography>
                      <Typography variant="caption" sx={{ color:'#9aa5b4' }}>{t.ssh_host} · {t.frontend_dir || '/var/www/...'}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm="auto">
            <Button variant="contained" startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
              onClick={generatePlan} disabled={!tenantId || generating}
              sx={{ borderRadius:'10px', textTransform:'none', fontWeight:700, backgroundColor:'#2f63ad', boxShadow:'none', height:40, '&:hover':{ backgroundColor:'#1f4f94', boxShadow:'none' } }}>
              {generating ? 'Analysing…' : 'Generate Plan'}
            </Button>
          </Grid>

          {planData && !planData.error && (
            <>
              <Grid item xs={12} sm="auto">
                <Button variant="contained" color="success"
                  startIcon={deploying ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                  onClick={deploy} disabled={!canDeploy}
                  sx={{ borderRadius:'10px', textTransform:'none', fontWeight:700, height:40, boxShadow:'none' }}>
                  {deploying ? 'Deploying…' : 'Deploy to Tenant'}
                </Button>
              </Grid>

              <Grid item xs={12} sm="auto">
                <FormControlLabel
                  control={<Switch size="small" checked={includeFE} onChange={e => setIncludeFE(e.target.checked)} />}
                  label={<Typography variant="caption" sx={{ fontWeight:600, color:'#475569' }}>Push Frontend</Typography>} />
              </Grid>
              <Grid item xs={12} sm="auto">
                <FormControlLabel
                  control={<Switch size="small" checked={includeSQL} onChange={e => setIncludeSQL(e.target.checked)} />}
                  label={<Typography variant="caption" sx={{ fontWeight:600, color:'#475569' }}>Run SQL</Typography>} />
              </Grid>
              <Grid item xs={12} sm="auto">
                <FormControlLabel
                  control={<Switch size="small" checked={mergeEnv} onChange={e => setMergeEnv(e.target.checked)} />}
                  label={<Typography variant="caption" sx={{ fontWeight:600, color:'#475569' }}>Sync .env</Typography>} />
              </Grid>
              {nodeMismatch && (
                <Grid item xs={12} sm="auto">
                  <FormControlLabel
                    control={<Switch size="small" checked={upgradeNode} onChange={e => setUpgradeNode(e.target.checked)} sx={{ '& .MuiSwitch-thumb': { backgroundColor: upgradeNode ? '#d97706' : undefined } }} />}
                    label={<Typography variant="caption" sx={{ fontWeight:600, color:'#d97706' }}>Upgrade Node ({planData.nodeVersion.tenant} → {planData.nodeVersion.demo})</Typography>} />
                </Grid>
              )}
            </>
          )}

          {tenantId && (
            <Grid item xs={12} sm="auto" sx={{ ml:{ sm:'auto' }, display:'flex', gap:1 }}>
              <Button variant="outlined" size="small" startIcon={syncingData ? <CircularProgress size={14} /> : <StorageIcon />}
                onClick={syncDataOnly} disabled={syncingData || !tenantId}
                sx={{ borderRadius:'10px', textTransform:'none', fontWeight:600, height:36 }}>
                Sync Data
              </Button>
              <Button variant="outlined" size="small" startIcon={pushingFE ? <CircularProgress size={14} /> : <WebIcon />}
                onClick={pushFrontendOnly} disabled={pushingFE || !tenantId}
                sx={{ borderRadius:'10px', textTransform:'none', fontWeight:600, height:36 }}>
                Frontend Only
              </Button>
              <Button variant="outlined" size="small" startIcon={syncingPerms ? <CircularProgress size={14} /> : <SecurityIcon />}
                onClick={syncPermissions} disabled={syncingPerms || !tenantId}
                sx={{ borderRadius:'10px', textTransform:'none', fontWeight:600, height:36, borderColor:'#0891b2', color:'#0891b2', '&:hover':{ borderColor:'#0e7490', backgroundColor:'rgba(8,145,178,0.04)' } }}>
                Sync Permissions
              </Button>
              <Button variant="outlined" size="small" startIcon={assigningTta ? <CircularProgress size={14} /> : <LockOpenIcon />}
                onClick={assignTtaadminPerms} disabled={assigningTta || !tenantId}
                sx={{ borderRadius:'10px', textTransform:'none', fontWeight:600, height:36, borderColor:'#16a34a', color:'#16a34a', '&:hover':{ borderColor:'#15803d', backgroundColor:'rgba(22,163,74,0.04)' } }}>
                Assign ttaadmin
              </Button>
              <Button variant="outlined" size="small" startIcon={grantingPerms ? <CircularProgress size={14} /> : <LockOpenIcon />}
                onClick={grantDbPermissions} disabled={grantingPerms || !tenantId}
                sx={{ borderRadius:'10px', textTransform:'none', fontWeight:600, height:36, borderColor:'#7c3aed', color:'#7c3aed', '&:hover':{ borderColor:'#6d28d9', backgroundColor:'rgba(124,58,237,0.04)' } }}>
                Grant DB Perms
              </Button>
            </Grid>
          )}
        </Grid>

        {generating && <LinearProgress sx={{ mt:2, borderRadius:4 }} />}

        {/* Deploy options hint */}
        {planData && !planData.error && selectedTenant && (
          <Box sx={{ mt:2, pt:2, borderTop:'1px solid rgba(47,99,173,0.06)', display:'flex', flexWrap:'wrap', gap:1 }}>
            <Chip icon={<CodeIcon sx={{ fontSize:'14px !important' }} />} label={`Backend → ${selectedTenant.app_dir || '/var/www/HOA'}`}
              size="small" sx={{ height:22, fontSize:'0.72rem', backgroundColor:'#f1f5f9', color:'#475569', borderRadius:'6px' }} />
            <Chip icon={<WebIcon sx={{ fontSize:'14px !important' }} />} label={`Frontend → ${selectedTenant.frontend_dir || '/var/www/demo'}`}
              size="small" sx={{ height:22, fontSize:'0.72rem', backgroundColor: includeFE ? '#dbeafe' : '#f1f5f9', color: includeFE ? '#1d4ed8' : '#94a3b8', borderRadius:'6px' }} />
            <Chip icon={<StorageIcon sx={{ fontSize:'14px !important' }} />} label="Auto-snapshot before deploy"
              size="small" sx={{ height:22, fontSize:'0.72rem', backgroundColor:'#dcfce7', color:'#166534', borderRadius:'6px' }} />
          </Box>
        )}
      </Paper>

      {/* ── Errors ── */}
      {planData?.error && <Alert severity="error" sx={{ borderRadius:'12px', mb:3 }}>{planData.error}</Alert>}

      {/* ── Node version mismatch ── */}
      {nodeMismatch && (
        <Alert severity="warning" sx={{ borderRadius:'12px', mb:2 }}>
          <strong>Node.js version mismatch:</strong> demo is on <strong>{planData.nodeVersion.demo}</strong> but this tenant is on <strong>{planData.nodeVersion.tenant}</strong>.<br />
          <Typography variant="caption">Enable "Upgrade Node" toggle above to automatically upgrade during deploy. This takes ~60s and requires internet access on the tenant.</Typography>
        </Alert>
      )}

      {/* ── DB warnings ── */}
      {planData?.dbWarnings?.map((w, i) => (
        <Alert key={i} severity="warning" sx={{ borderRadius:'12px', mb:2 }}>
          <strong>DB not reachable:</strong> {w}<br />
          <Typography variant="caption">Configure DB1_SERVER, DB1_USER, DB1_PASSWORD, DB1_NAME in the tenant's .env and re-run. File sync still works without DB access.</Typography>
        </Alert>
      ))}

      {/* ── In sync banner ── */}
      {inSync && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ borderRadius:'12px', mb:3, fontWeight:600 }}>
          {selectedTenant?.name} is fully in sync with demo - no changes detected.
          {includeFE && ' You can still push the frontend using "Deploy to Tenant" or "Frontend Only".'}
        </Alert>
      )}

      {/* ── Plan summary cards ── */}
      {planData && !planData.error && (
        <Box>
          {/* Summary row */}
          <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap', mb:2.5 }}>
            {[
              { label:`${plan?.renames?.length||0} table renames`,  bg:'#ede9fe', color:'#5b21b6', show: true },
              { label:`${totalColsToAdd} columns to add`,           bg:'#fef3c7', color:'#92400e', show: true },
              { label:`${plan?.missingTables?.length||0} missing tables`, bg:'#fee2e2', color:'#991b1b', show: true },
              { label:`${planData.filesToPush?.filter(f=>f.category==='backend').length||0} backend files`, bg:'#dbeafe', color:'#1d4ed8', show: true },
              { label:`Frontend ${includeFE ? 'will be synced' : 'skipped'}`, bg: includeFE ? '#dcfce7' : '#f1f5f9', color: includeFE ? '#166534' : '#94a3b8', show: true },
              { label:`${plan?.extraTables?.length||0} extra tables on tenant (ignored)`, bg:'#f1f5f9', color:'#64748b', show: (plan?.extraTables?.length||0)>0 },
              { label:`${planData.summary?.systemTables||0} system tables · ${planData.summary?.systemRows||0} rows`, bg:'#ecfdf5', color:'#065f46', show: (planData.summary?.systemRows||0)>0 },
              { label:`${(planData.envChanges?.added?.length||0)+(planData.envChanges?.extracted?.length||0)} .env keys to sync`, bg:'#fdf4ff', color:'#7e22ce', show: ((planData.envChanges?.added?.length||0)+(planData.envChanges?.extracted?.length||0))>0 },
              { label:`${planData.envChanges?.missing?.length||0} .env keys missing (manual)`, bg:'#fee2e2', color:'#991b1b', show: (planData.envChanges?.missing?.length||0)>0 },
            ].filter(i => i.show).map(i => (
              <Chip key={i.label} label={i.label} size="small"
                sx={{ height:24, fontSize:'0.75rem', fontWeight:700, backgroundColor:i.bg, color:i.color, borderRadius:'6px' }} />
            ))}
          </Box>

          {/* Schema sections */}
          {planData.schemaAvailable !== false && (
            <>
              <SectionPanel title="Table Renames" icon={<StorageIcon sx={{ color:'#5b21b6', fontSize:20 }} />}
                count={plan?.renames?.length||0} countBg="#ede9fe" countColor="#5b21b6"
                defaultOpen={(plan?.renames?.length||0)>0}>
                <Alert severity="info" sx={{ mb:2, py:0.5, fontSize:'0.8rem', borderRadius:'8px' }}>
                  Uses <code>sp_rename</code> - renames the table in-place. No data moved. Fully reversible via rollback.
                </Alert>
                <RenamesTable renames={plan?.renames} />
              </SectionPanel>

              <SectionPanel title="Missing Columns" icon={<StorageIcon sx={{ color:'#92400e', fontSize:20 }} />}
                count={totalColsToAdd} countBg="#fef3c7" countColor="#92400e"
                defaultOpen={totalColsToAdd>0}>
                <Alert severity="warning" sx={{ mb:2, py:0.5, fontSize:'0.8rem', borderRadius:'8px' }}>
                  All new columns are added as <strong>NULL</strong> so existing rows are never broken.
                </Alert>
                <AddColumnsTable addColumns={plan?.addColumns} />
              </SectionPanel>

              <SectionPanel title="Missing Tables" icon={<StorageIcon sx={{ color:'#991b1b', fontSize:20 }} />}
                count={plan?.missingTables?.length||0} countBg="#fee2e2" countColor="#991b1b"
                defaultOpen={(plan?.missingTables?.length||0)>0}>
                <Alert severity="success" sx={{ mb:2, py:0.5, fontSize:'0.8rem', borderRadius:'8px' }}>
                  <strong>CREATE TABLE statements are auto-generated</strong> from the demo schema and included in the migration SQL. Each uses <code>IF NOT EXISTS</code> so it is safe to re-run. Primary keys and IDENTITY columns are detected automatically.
                </Alert>
                <MissingTablesTable missingTables={plan?.missingTables} />
              </SectionPanel>
            </>
          )}

          {/* System table data */}
          {planData.systemTablePlan?.length > 0 && (
            <SectionPanel title="System Table Data Sync" icon={<StorageIcon sx={{ color:'#065f46', fontSize:20 }} />}
              count={planData.summary?.systemRows || 0} countBg="#ecfdf5" countColor="#065f46"
              defaultOpen={(planData.summary?.systemRows||0) > 0}>
              <Alert severity="success" sx={{ mb:2, py:0.5, fontSize:'0.8rem', borderRadius:'8px' }}>
                These tables contain configuration and template data that should be identical across all tenants.
                Rows are synced using <code>MERGE</code> (upsert) - existing rows are updated, missing rows are inserted. No data is deleted.
              </Alert>
              <Table size="small">
                <TableHead><TableRow sx={{ backgroundColor:'#f8fafc' }}>
                  <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Table</TableCell>
                  <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Rows on demo</TableCell>
                  <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Status</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {planData.systemTablePlan.map(t => (
                    <TableRow key={t.name}>
                      <TableCell sx={{ fontFamily:'monospace', fontSize:'0.78rem', color:'#1e3a5f', fontWeight: t.isNew ? 700 : 400 }}>{t.name}</TableCell>
                      <TableCell sx={{ fontSize:'0.75rem', color: t.count > 0 ? '#065f46' : '#94a3b8', fontWeight: t.count > 0 ? 700 : 400 }}>{t.count}</TableCell>
                      <TableCell>
                        {t.isNew
                          ? <Chip label="NEW TABLE + data" size="small" sx={{ height:20, fontSize:'0.65rem', backgroundColor:'#dbeafe', color:'#1d4ed8', borderRadius:'5px' }} />
                          : t.count > 0
                            ? <Chip label="MERGE (upsert)" size="small" sx={{ height:20, fontSize:'0.65rem', backgroundColor:'#dcfce7', color:'#166534', borderRadius:'5px' }} />
                            : <Chip label="empty" size="small" sx={{ height:20, fontSize:'0.65rem', backgroundColor:'#f1f5f9', color:'#94a3b8', borderRadius:'5px' }} />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </SectionPanel>
          )}

          {/* .env section */}
          {planData.envChanges && (
            <SectionPanel title=".env Configuration Sync" icon={<StorageIcon sx={{ color:'#7e22ce', fontSize:20 }} />}
              count={(planData.envChanges.added?.length||0)+(planData.envChanges.extracted?.length||0)+(planData.envChanges.missing?.length||0)}
              countBg="#fdf4ff" countColor="#7e22ce"
              defaultOpen={(planData.envChanges.added?.length||0)+(planData.envChanges.extracted?.length||0)+(planData.envChanges.missing?.length||0)>0}>

              {planData.envChanges.missing?.length > 0 && (
                <Alert severity="error" sx={{ mb:2, py:0.5, fontSize:'0.8rem', borderRadius:'8px' }}>
                  <strong>{planData.envChanges.missing.length} required key(s) still missing</strong> - must be filled manually on the tenant:<br />
                  <code>{planData.envChanges.missing.join(', ')}</code><br />
                  SSH into the tenant and set these in <code>{selectedTenant?.app_dir || '/var/www/HOA'}/.env</code>
                </Alert>
              )}

              {planData.envChanges.extracted?.length > 0 && (
                <Alert severity="success" sx={{ mb:2, py:0.5, fontSize:'0.8rem', borderRadius:'8px' }}>
                  <strong>Recovered from legacy hardcoded files:</strong> {planData.envChanges.extracted.join(', ')}<br />
                  These were hardcoded in old files (e.g. dbMonitor.js) and will be moved into .env properly.
                </Alert>
              )}

              {planData.envChanges.added?.length > 0 && (
                <Alert severity="info" sx={{ mb:2, py:0.5, fontSize:'0.8rem', borderRadius:'8px' }}>
                  <strong>New keys from demo to add:</strong> {planData.envChanges.added.join(', ')}
                </Alert>
              )}

              {planData.envChanges.updated?.length > 0 && (
                <Box sx={{ mb:2 }}>
                  <Typography variant="caption" sx={{ fontWeight:700, color:'#6b778c', textTransform:'uppercase', letterSpacing:0.5 }}>Domain keys (auto-corrected to tenant's domain)</Typography>
                  {planData.envChanges.updated.map(u => (
                    <Box key={u.key} sx={{ display:'flex', gap:1, alignItems:'center', py:0.3 }}>
                      <Typography sx={{ fontFamily:'monospace', fontSize:'0.75rem', color:'#6b778c', minWidth:200 }}>{u.key}</Typography>
                      <Typography sx={{ fontFamily:'monospace', fontSize:'0.72rem', color:'#dc2626', textDecoration:'line-through' }}>{u.from}</Typography>
                      <Typography sx={{ fontFamily:'monospace', fontSize:'0.72rem', color:'#16a34a' }}>→ {u.to}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Typography variant="caption" sx={{ color:'#94a3b8', fontStyle:'italic' }}>
                DB credentials (DB1_*, DB2_*) and secrets (JWT_*, SESSION_*) are always preserved from the tenant - never overwritten from demo.
              </Typography>
            </SectionPanel>
          )}

          {/* Files section */}
          <SectionPanel title="Backend Files to Push" icon={<CodeIcon sx={{ color:'#1d4ed8', fontSize:20 }} />}
            count={planData.filesToPush?.filter(f=>f.category==='backend').length||0} countBg="#dbeafe" countColor="#1d4ed8"
            defaultOpen={(planData.filesToPush?.length||0)>0}>
            <FilesToPushTable filesToPush={planData.filesToPush} />
          </SectionPanel>

          {/* Generated SQL (collapsed by default) */}
          <SectionPanel title="Generated Migration SQL (review before deploying)" icon={<CodeIcon sx={{ color:'#475569', fontSize:20 }} />}>
            <Box sx={{ backgroundColor:'#0f1f35', borderRadius:'10px', p:2, maxHeight:300, overflowY:'auto' }}>
              <pre style={{ margin:0, fontSize:'0.74rem', color:'#e2e8f0', fontFamily:'monospace', whiteSpace:'pre-wrap', lineHeight:1.6 }}>{planData.sql}</pre>
            </Box>
          </SectionPanel>

          <SectionPanel title="Rollback SQL (stored in snapshot automatically)" icon={<UndoIcon sx={{ color:'#475569', fontSize:20 }} />}>
            <Alert severity="info" sx={{ mb:2, py:0.5, fontSize:'0.8rem', borderRadius:'8px' }}>
              This SQL is stored in the snapshot at deploy time. Click "Rollback" on any snapshot below to reverse all changes.
            </Alert>
            <Box sx={{ backgroundColor:'#0f1f35', borderRadius:'10px', p:2, maxHeight:200, overflowY:'auto' }}>
              <pre style={{ margin:0, fontSize:'0.74rem', color:'#e2e8f0', fontFamily:'monospace', whiteSpace:'pre-wrap', lineHeight:1.6 }}>{planData.rollbackSql}</pre>
            </Box>
          </SectionPanel>
        </Box>
      )}

      {/* ── Snapshots / rollback ── */}
      {tenantId && snapshots.length > 0 && (
        <Paper elevation={0} sx={{ borderRadius:'16px', border:'1px solid rgba(47,99,173,0.08)', p:3, mt:1 }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:2 }}>
            <FolderOpenIcon sx={{ color:'#6b778c', fontSize:20 }} />
            <Typography sx={{ fontWeight:700, color:'#1e3a5f' }}>Snapshots &amp; Rollback</Typography>
            <Chip label={snapshots.length} size="small" sx={{ height:20, fontSize:'0.68rem', backgroundColor:'#e2e8f0', color:'#475569', borderRadius:'5px' }} />
            <Typography variant="caption" sx={{ color:'#9aa5b4', ml:0.5 }}>Stored on tenant - each deploy creates one automatically</Typography>
          </Box>
          <Table size="small">
            <TableHead><TableRow sx={{ backgroundColor:'#f8fafc' }}>
              <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Created</TableCell>
              <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Snapshot ID</TableCell>
              <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Action</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {snapshots.map((s, idx) => (
                <TableRow key={s.id}>
                  <TableCell sx={{ fontSize:'0.78rem', color:'#1e3a5f', fontWeight: idx===0 ? 700 : 400 }}>
                    {new Date(s.createdAt).toLocaleString('en-US', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}
                    {idx === 0 && <Chip label="latest" size="small" sx={{ height:18, fontSize:'0.62rem', ml:1, backgroundColor:'#dcfce7', color:'#166534', borderRadius:'4px' }} />}
                  </TableCell>
                  <TableCell sx={{ fontFamily:'monospace', fontSize:'0.72rem', color:'#64748b' }}>{s.id}</TableCell>
                  <TableCell>
                    <Tooltip title="Restore backend files and reverse all SQL changes from this snapshot">
                      <Button size="small" variant="outlined" color="warning"
                        startIcon={rollingBack ? <CircularProgress size={14} /> : <UndoIcon />}
                        onClick={() => rollback(s.id)} disabled={rollingBack}
                        sx={{ borderRadius:'8px', textTransform:'none', fontWeight:700, fontSize:'0.75rem' }}>
                        Rollback to this
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <LogDialog open={logDialog.open} loading={logDialog.loading}
        title={logDialog.title} log={logDialog.log}
        onClose={() => setLogDialog(d => ({ ...d, open:false }))} />
    </Box>
  );
}
